import type { PropsWithChildren } from "react";
import { createContext, useCallback, useMemo, useState } from "react";
import type { ClientInfo, CompanyInfo } from "../api/types/clientTypes";
import type { ClientPermissionSet, UserInfo } from "../api/types/userTypes";
import storage from "../storage/storage";

const UserContext = createContext<ContextValue | undefined>(undefined);

interface Props {
  userInfo: UserInfo;
  permissions: ClientPermissionSet[];
  userClients: ClientInfo[];
  refreshUserData: () => void;
}

interface ContextValue extends UserInfo {
  permissions: ClientPermissionSet[];
  clients: ClientInfo[];
  isBetaAccessAvailable: boolean;
  isBetaAccessEnabled: boolean;
  refreshUserData: () => void;
  updateClientData: (clientCode: string, clientData: CompanyInfo) => void;
  updateBetaAccessEnabled: (value: boolean) => void;
}

export const UserContextProvider = ({
  userInfo,
  permissions,
  userClients,
  refreshUserData,
  children,
}: PropsWithChildren<Props>) => {
  const [clients, setClients] = useState<ClientInfo[]>(userClients);

  const [betaAccessEnabled, setBetaAccessEnabled] = useState(
    storage.getBetaAccessEnabled() ?? userInfo.betaAccess?.enabledByDefault === true
  );

  const updateClientData = useCallback((clientCode: string, clientData: CompanyInfo) => {
    setClients((prev) =>
      prev.map((client) => (client.clientCode === clientCode ? { ...client, ...clientData } : client))
    );
  }, []);

  const updateBetaAccessEnabled = useCallback((enabled: boolean) => {
    setBetaAccessEnabled(enabled);
    storage.setBetaAccessEnabled(enabled);
  }, []);

  const isBetaAccessAvailable = useMemo(
    () => userInfo.betaAccess?.available === true,
    [userInfo.betaAccess?.available]
  );

  const isBetaAccessEnabled = useMemo(
    () => betaAccessEnabled && isBetaAccessAvailable,
    [betaAccessEnabled, isBetaAccessAvailable]
  );

  return (
    <UserContext
      value={{
        ...userInfo,
        permissions,
        clients,
        isBetaAccessAvailable,
        isBetaAccessEnabled,
        refreshUserData,
        updateClientData,
        updateBetaAccessEnabled,
      }}
    >
      {children}
    </UserContext>
  );
};

export default UserContext;
